import { onScopeDispose, ref, type Ref } from 'vue'

export interface ColumnResizeInput {
  leftWidth: number
  rightWidth: number
  containerWidth: number
  update: (leftWidth: number, rightWidth: number) => void
}

export interface UseColumnResize {
  resizing: Readonly<Ref<boolean>>
  start: (event: PointerEvent, input: ColumnResizeInput) => void
  stop: () => void
}

/** v3 `CustomDashboardSection.vue:548` — neither column of a pair may fall below 20%. */
const MIN_WIDTH = 20

/**
 * The two-column drag handle, ported from v3 `initResize` (`CustomDashboardSection.vue:536-572`).
 *
 * Every width write is rAF-throttled and the gesture takes pointer capture, both of which v3 did
 * and the first v4 port dropped: without the throttle each `pointermove` pushed a fresh
 * `update:rows` through `resizeRowCells`, which deep-clones the whole dashboard tree
 * (`serializeLayout` → `parseLayout`) and re-renders every widget — once per input event rather
 * than once per frame.
 *
 * Two v3 defects are NOT ported:
 *
 *  1. `onUp` ran `cancelAnimationFrame(rafId)` before persisting (`:566`), so the last queued
 *     frame was discarded and a drag released inside one frame of its final move persisted a
 *     stale width — or, for a short drag, no width change at all. Here the drop FLUSHES the
 *     pending write instead, so the columns land where the pointer was released.
 *  2. v3 listened for `pointerup` only. A gesture the browser cancels (`pointercancel` — touch
 *     scroll takeover, a context menu) never removed its `pointermove` listener, leaving the
 *     column resizing on a pointer the user was no longer holding down.
 */
export function useColumnResize(): UseColumnResize {
  const resizing = ref(false)
  let move: ((event: PointerEvent) => void) | null = null
  let end: (() => void) | null = null
  let frame: number | null = null
  let pending: (() => void) | null = null
  let releaseCapture: (() => void) | null = null

  function cancelFrame(): void {
    if (frame !== null) window.cancelAnimationFrame(frame)
    frame = null
  }

  /** Run the newest queued write now rather than waiting for a frame that may never arrive. */
  function flush(): void {
    cancelFrame()
    const queued = pending
    pending = null
    queued?.()
  }

  function detach(): void {
    if (move) window.removeEventListener('pointermove', move)
    if (end) {
      window.removeEventListener('pointerup', end)
      window.removeEventListener('pointercancel', end)
    }
    move = null
    end = null
    releaseCapture?.()
    releaseCapture = null
    resizing.value = false
  }

  /** Abandon the gesture: the queued width is dropped, unlike the `pointerup` path. */
  function stop(): void {
    cancelFrame()
    pending = null
    detach()
  }

  function start(event: PointerEvent, input: ColumnResizeInput): void {
    stop()
    resizing.value = true
    const startX = event.clientX
    const total = input.leftWidth + input.rightWidth
    const maximum = total - MIN_WIDTH

    // Capture keeps the pointer stream on the handle when the cursor outruns it or crosses an
    // iframe/widget that would otherwise swallow the move events mid-drag.
    const handle = event.currentTarget
    if (handle instanceof Element && typeof handle.setPointerCapture === 'function') {
      handle.setPointerCapture(event.pointerId)
      releaseCapture = () => {
        if (handle.hasPointerCapture?.(event.pointerId) === true) {
          handle.releasePointerCapture(event.pointerId)
        }
      }
    }

    move = (next) => {
      const delta = ((next.clientX - startX) / Math.max(input.containerWidth, 1)) * 100
      const left = Math.min(maximum, Math.max(MIN_WIDTH, input.leftWidth + delta))
      // Widths are always derived from the gesture's ORIGIN, never from the last emitted value, so
      // coalescing several moves into one frame cannot drift.
      pending = () => input.update(left, total - left)
      if (frame !== null) return
      frame = window.requestAnimationFrame(() => {
        frame = null
        flush()
      })
    }

    end = () => {
      flush()
      detach()
    }

    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', end, { once: true })
    window.addEventListener('pointercancel', end, { once: true })
  }

  onScopeDispose(stop)
  return { resizing, start, stop }
}
